import React, { useRef } from "react";
import PropTypes from "prop-types";
import { uniqueId } from "lodash";

import TextAreaInput from "../../../../components/form/TextAreaInput";
import CharCounter from "../../../../components/form/CharCounter";
import { commitHaptic } from "../haptics";
import { useDraft } from "../useDraft";
import AnalysisFrame from "./AnalysisFrame";
import TopikAssistPanel from "./TopikAssistPanel";

// A one-textarea analysis step (risk, solution, risk-mitigation, solution-maximizer). Owns
// its local draft (prefilled for backtracking) and the capped TextAreaInput + counter; the
// concrete views supply copy, the submit handler, and (via assistStep) the AI companion.
const SingleTextAnalysisStep = ({
  t,
  title,
  instruction,
  placeholder,
  submitLabel,
  initialValue,
  draftKey,
  maxLength,
  submitting,
  gameClosed,
  error,
  recall = null,
  assistStep = null,
  onBack,
  onSubmit,
}) => {
  // Draft persists across reload/rejoin (§B6); falls back to the server value when no draft.
  const [value, setValue] = useDraft(draftKey, initialValue);
  const titleId = useRef(uniqueId("topik-step-title-")).current;
  const instructionId = useRef(uniqueId("topik-step-instruction-")).current;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!value.trim() || submitting || gameClosed) return;
    commitHaptic();
    onSubmit(value.trim());
  };

  return (
    <AnalysisFrame
      t={t}
      title={title}
      titleId={titleId}
      instruction={instruction}
      instructionId={instructionId}
      canBack
      submitting={submitting}
      gameClosed={gameClosed}
      error={error}
      onBack={onBack}
    >
      {recall}
      <form className="topik-view__form" onSubmit={handleSubmit}>
        <div className="topik-analysis__textarea-wrapper">
          <TextAreaInput
            className="topik-textarea"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            minRows={3}
            maxLength={maxLength}
            inputProps={{
              disabled: submitting,
              "aria-labelledby": titleId,
              "aria-describedby": instruction ? instructionId : undefined,
            }}
          />
          <CharCounter value={value} maxLength={maxLength} nudge={t("topik-view.counter-nudge")} />
        </div>
        {/* The graduated AI companion (§6.5): a nudge first, ideas only on the second press. */}
        {assistStep && <TopikAssistPanel step={assistStep} draft={value} />}
        <button
          type="submit"
          className="topik-view__submit-button"
          disabled={!value.trim() || submitting || gameClosed}
        >
          {submitLabel}
        </button>
      </form>
    </AnalysisFrame>
  );
};

SingleTextAnalysisStep.propTypes = {
  t: PropTypes.func.isRequired,
  title: PropTypes.node.isRequired,
  instruction: PropTypes.node,
  placeholder: PropTypes.string,
  submitLabel: PropTypes.node.isRequired,
  initialValue: PropTypes.string,
  draftKey: PropTypes.string,
  maxLength: PropTypes.number.isRequired,
  submitting: PropTypes.bool,
  gameClosed: PropTypes.bool,
  error: PropTypes.any,
  recall: PropTypes.node,
  assistStep: PropTypes.string,
  onBack: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

SingleTextAnalysisStep.defaultProps = {
  instruction: null,
  placeholder: "",
  initialValue: "",
  draftKey: undefined,
  submitting: false,
  gameClosed: false,
  error: null,
  recall: null,
  assistStep: null,
};

export default SingleTextAnalysisStep;
